import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, InputLabel, Select, MenuItem, TextField, 
    Card, Icon, IconButton, Tooltip, Table, TableBody, TableRow, Checkbox, Tabs, Tab, Badge, Chip,
    Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState, useEffect, useCallback } from "react";
import { get, post } from "utils/WebService";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";

export default function LoteAssociacaoModal({ open, handleClose, marcacaoId, marcacaoObj }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userName } = useUser();

    // Passos do Stepper
    const [currentStep, setCurrentStep] = useState(1); // 1: Filtro/Upload, 2: Visualizar, 3: Marcação, 4: Concluído
    const [inputTab, setInputTab] = useState(0); // 0: Planilha/IDs, 1: Filtros de Tela

    // Tipos e dados da busca
    const [tipoAlvo, setTipoAlvo] = useState(marcacaoObj?.cvAlvo === 2 ? "contratos" : "devedores");
    const [rawIdsInput, setRawIdsInput] = useState("");
    const [selectedFileName, setSelectedFileName] = useState("");

    // Dados das marcacoes especiais ativas
    const [marcacoesList, setMarcacoesList] = useState([]);
    const [selectedMarcacaoId, setSelectedMarcacaoId] = useState(marcacaoId || "");
    const [tipoAcao, setTipoAcao] = useState("ADICIONAR"); // "ADICIONAR" ou "REMOVER"
    const [observacao, setObservacao] = useState("");

    // Tabela de Preview (Resultados)
    const [previewList, setPreviewList] = useState([]);
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingExecucao, setLoadingExecucao] = useState(false);

    // Histórico de Execuções em Lote
    const [historicoLogs, setHistoricoLogs] = useState([]);
    const [loadingHistorico, setLoadingHistorico] = useState(false);
    const [logDetailOpen, setLogDetailOpen] = useState(false);
    const [selectedLogDetail, setSelectedLogDetail] = useState(null);

    // Notificações / Toast
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    useEffect(() => {
        if (open) {
            fetchMarcacoes();
            fetchHistorico();
            if (marcacaoId) {
                setSelectedMarcacaoId(marcacaoId);
            }
        }
    }, [open, marcacaoId]);

    const fetchMarcacoes = () => {
        get("/marcacoes").then((data) => {
            setMarcacoesList(Array.isArray(data) ? data : []);
        }).catch(() => setMarcacoesList([]));
    };

    const fetchHistorico = () => {
        setLoadingHistorico(true);
        get("/marcacoes/lote/historico").then((data) => {
            setHistoricoLogs(Array.isArray(data) ? data : []);
            setLoadingHistorico(false);
        }).catch(() => setLoadingHistorico(false));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            // Extrai números e códigos linha a linha ou separados por vírgula/ponto e vírgula
            const extracted = content.split(/[\r\n,;]+/).map(s => s.trim()).filter(Boolean).join(", ");
            setRawIdsInput(extracted);
        };
        reader.readAsText(file);
    };

    const handleProcessarPreview = () => {
        if (!rawIdsInput.trim()) {
            setSbMessage("Informe os números ou suba uma planilha com os IDs.");
            setErrorSB(true);
            return;
        }

        const chaves = rawIdsInput.split(/[\s,;]+/).map(i => i.trim()).filter(Boolean);
        if (chaves.length === 0) {
            setSbMessage("Nenhum código válido encontrado.");
            setErrorSB(true);
            return;
        }

        setLoadingPreview(true);
        post("/marcacoes/lote/preview", {
            tipoBusca: tipoAlvo,
            chaves: chaves
        }).then((data) => {
            const list = Array.isArray(data) ? data : [];
            setPreviewList(list);
            // Por padrão, seleciona todas as fichas encontradas
            const allIds = new Set(list.map(item => item.id));
            setSelectedRowIds(allIds);
            setLoadingPreview(false);
            if (list.length > 0) {
                setCurrentStep(2);
            } else {
                setSbMessage("Nenhum registro encontrado no sistema com os IDs informados.");
                setErrorSB(true);
            }
        }).catch(() => {
            setLoadingPreview(false);
            setSbMessage("Erro ao buscar dados das fichas no servidor.");
            setErrorSB(true);
        });
    };

    const handleToggleSelectAll = () => {
        if (selectedRowIds.size === previewList.length) {
            setSelectedRowIds(new Set());
        } else {
            setSelectedRowIds(new Set(previewList.map(item => item.id)));
        }
    };

    const handleToggleSelectRow = (id) => {
        const next = new Set(selectedRowIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedRowIds(next);
    };

    const handleAplicarLote = () => {
        if (!selectedMarcacaoId) {
            setSbMessage("Selecione a Marcação Especial que deseja aplicar.");
            setErrorSB(true);
            return;
        }

        if (selectedRowIds.size === 0) {
            setSbMessage("Selecione pelo menos um registro na tabela para aplicar a marcação.");
            setErrorSB(true);
            return;
        }

        setLoadingExecucao(true);
        setCurrentStep(3);

        const payload = {
            idMarcacao: selectedMarcacaoId,
            tipoAcao: tipoAcao,
            tipoAlvo: tipoAlvo,
            ids: Array.from(selectedRowIds),
            observacao: observacao
        };

        post("/marcacoes/lote/executar", payload).then((res) => {
            setLoadingExecucao(false);
            setCurrentStep(4);
            setSbMessage(`Marcação em bloco executada com sucesso! (${res?.qtdeMarcada || selectedRowIds.size} processados)`);
            setSuccessSB(true);
            fetchHistorico();
        }).catch(() => {
            setLoadingExecucao(false);
            setCurrentStep(2);
            setSbMessage("Erro ao executar marcação em bloco.");
            setErrorSB(true);
        });
    };

    const handleResetAll = () => {
        setCurrentStep(1);
        setRawIdsInput("");
        setSelectedFileName("");
        setPreviewList([]);
        setSelectedRowIds(new Set());
        setObservacao("");
    };

    const getStatusChip = (status) => {
        if (status === "CONCLUIDO") {
            return <Chip label="Concluído" color="success" size="small" sx={{ fontWeight: "bold", color: "#fff" }} />;
        }
        if (status === "CONCLUIDO_COM_PENDENCIAS") {
            return <Chip label="Com Pendências" color="warning" size="small" sx={{ fontWeight: "bold", color: "#fff" }} />;
        }
        return <Chip label="Erro" color="error" size="small" sx={{ fontWeight: "bold", color: "#fff" }} />;
    };

    const selectedMarcacaoObj = marcacoesList.find(m => m.cvId === selectedMarcacaoId) || marcacaoObj;

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="xl" 
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? "#363945" : "#f4f6f9",
                    color: darkMode ? "#ffffff" : "inherit",
                    minHeight: "85vh"
                }
            }}
        >
            {/* Cabeçalho Modal & Stepper */}
            <DialogTitle sx={{ backgroundColor: darkMode ? "#424550" : "#ffffff", pb: 1, borderBottom: darkMode ? "1px solid #555" : "1px solid #eee" }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox>
                        <MDTypography variant="h5" color={darkMode ? "white" : "dark"} fontWeight="bold">
                            Marcação de Fichas em Bloco
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                            Selecione os filtros ou planilha, visualize as fichas e aplique a marcação em lote.
                        </MDTypography>
                    </MDBox>
                    <IconButton onClick={handleClose} sx={{ color: darkMode ? "#fff" : "#666" }}>
                        <Icon>close</Icon>
                    </IconButton>
                </MDBox>

                {/* Barra de Progresso Stepper (1, 2, 3, 4) */}
                <MDBox display="flex" justifyContent="center" alignItems="center" mt={2} mb={1} px={4}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <AvatarStep number="1" active={currentStep >= 1} current={currentStep === 1} label="Filtrar / Upload" darkMode={darkMode} />
                        <MDBox sx={{ width: 60, height: 2, backgroundColor: currentStep >= 2 ? "#007bff" : (darkMode ? "#555" : "#ccc") }} />
                        <AvatarStep number="2" active={currentStep >= 2} current={currentStep === 2} label="Visualizar Resultados" darkMode={darkMode} />
                        <MDBox sx={{ width: 60, height: 2, backgroundColor: currentStep >= 3 ? "#007bff" : (darkMode ? "#555" : "#ccc") }} />
                        <AvatarStep number="3" active={currentStep >= 3} current={currentStep === 3} label="Aplicar Marcação" darkMode={darkMode} />
                        <MDBox sx={{ width: 60, height: 2, backgroundColor: currentStep >= 4 ? "#007bff" : (darkMode ? "#555" : "#ccc") }} />
                        <AvatarStep number="4" active={currentStep >= 4} current={currentStep === 4} label="Conclusão" darkMode={darkMode} />
                    </MDBox>
                </MDBox>
            </DialogTitle>

            <DialogContent sx={{ p: 2.5 }}>
                {/* 1. SEÇÃO DE ENTRADA / PESQUISA E UPLOAD */}
                <Card sx={{ p: 2, mb: 2, background: darkMode ? "#4B4F5C" : "#ffffff" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"} display="flex" alignItems="center" gap={1}>
                            <Icon color="info">list_alt</Icon> Critérios de Busca e Planilha
                        </MDTypography>
                        {previewList.length > 0 && (
                            <MDButton size="small" variant="outlined" color="secondary" onClick={handleResetAll} sx={{ color: darkMode ? "#fff !important" : undefined, borderColor: darkMode ? "#fff !important" : undefined }}>
                                Limpar Busca / Nova Planilha
                            </MDButton>
                        )}
                    </MDBox>

                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ color: darkMode ? "#ffffff !important" : "inherit", fontWeight: "bold", mb: 0.5 }}>Identificar Por</InputLabel>
                            <Select
                                fullWidth
                                size="small"
                                value={tipoAlvo}
                                onChange={(e) => setTipoAlvo(e.target.value)}
                                sx={{
                                    height: 40,
                                    backgroundColor: darkMode ? "#2D3039" : "#ffffff",
                                    color: darkMode ? "#ffffff !important" : "inherit",
                                    "& .MuiSelect-icon": { color: darkMode ? "#ffffff !important" : "inherit" },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: darkMode ? "#007bff !important" : undefined
                                    }
                                }}
                            >
                                <MenuItem value="devedores">ID da Ficha / Devedor (Recomendado)</MenuItem>
                                <MenuItem value="contratos">Número do Contrato / Título</MenuItem>
                            </Select>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <InputLabel sx={{ color: darkMode ? "#ffffff !important" : "inherit", fontWeight: "bold", mb: 0.5 }}>
                                Planilha Excel/CSV ou Lista de IDs (separados por vírgula)
                            </InputLabel>
                            <MDBox display="flex" gap={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Ex: 10021, 10022, 10025..."
                                    value={rawIdsInput}
                                    onChange={(e) => setRawIdsInput(e.target.value)}
                                    sx={{
                                        backgroundColor: darkMode ? "#2D3039" : "#ffffff",
                                        borderRadius: 1,
                                        "& .MuiInputBase-input": { color: darkMode ? "#ffffff !important" : "inherit" },
                                        "& .MuiInputBase-input::placeholder": {
                                            color: darkMode ? "rgba(255, 255, 255, 0.6) !important" : "inherit",
                                            opacity: 1
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: darkMode ? "#007bff !important" : undefined
                                        }
                                    }}
                                />
                                <MDButton 
                                    variant="contained" 
                                    color="info" 
                                    component="label"
                                    sx={{ 
                                        whiteSpace: "nowrap",
                                        backgroundColor: darkMode ? "#0288d1 !important" : undefined,
                                        color: "#ffffff !important",
                                        fontWeight: "bold",
                                        height: 40
                                    }}
                                >
                                    <Icon sx={{ mr: 0.5 }}>upload_file</Icon> Subir Planilha
                                    <input type="file" hidden accept=".csv, .xlsx, .xls, .txt" onChange={handleFileUpload} />
                                </MDButton>
                            </MDBox>
                            {selectedFileName && (
                                <MDTypography variant="caption" color="info" mt={0.5} display="block" sx={{ color: darkMode ? "#90caf9" : undefined }}>
                                    Arquivo carregado: <strong>{selectedFileName}</strong>
                                </MDTypography>
                            )}
                        </Grid>

                        <Grid item xs={12} sm={3} display="flex" justifyContent="flex-end">
                            <MDButton 
                                variant="contained" 
                                color="info" 
                                fullWidth
                                onClick={handleProcessarPreview} 
                                disabled={loadingPreview}
                                sx={{
                                    backgroundColor: darkMode ? "#007bff !important" : undefined,
                                    color: "#ffffff !important",
                                    height: 40,
                                    fontWeight: "bold",
                                    boxShadow: darkMode ? "0 4px 12px rgba(0, 123, 255, 0.4)" : undefined
                                }}
                            >
                                <Icon sx={{ mr: 0.5 }}>search</Icon>
                                {loadingPreview ? "Buscando..." : "Pesquisar & Carregar"}
                            </MDButton>
                        </Grid>
                    </Grid>
                </Card>

                {/* 2. ÁREA DE RESULTADOS (TABELA DE PREVIEW) E PAINEL DE AÇÃO */}
                <Grid container spacing={2}>
                    {/* COLUNA ESQUERDA: TABELA DE RESULTADOS */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ p: 2, background: darkMode ? "#424550" : "#ffffff", minHeight: 420 }}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <MDTypography variant="h6" color={darkMode ? "white" : "dark"} display="flex" alignItems="center" gap={1}>
                                    <Icon color="info">view_list</Icon> Resultado da Pesquisa
                                    {previewList.length > 0 && (
                                        <Chip 
                                            label={`${previewList.length} fichas encontradas`} 
                                            color="info" 
                                            size="small" 
                                            sx={{ fontWeight: "bold", ml: 1 }} 
                                        />
                                    )}
                                </MDTypography>

                                {previewList.length > 0 && (
                                    <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                                        <strong>{selectedRowIds.size}</strong> de <strong>{previewList.length}</strong> selecionadas
                                    </MDTypography>
                                )}
                            </MDBox>

                            {previewList.length === 0 ? (
                                <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} opacity={0.7}>
                                    <Icon sx={{ fontSize: "48px !important", mb: 1, color: darkMode ? "#90caf9" : "inherit" }}>search_off</Icon>
                                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                                        Nenhuma ficha carregada. Insira os IDs ou envie uma planilha acima e clique em "Pesquisar & Carregar".
                                    </MDTypography>
                                </MDBox>
                            ) : (
                                <MDBox overflow="auto" maxHeight={360}>
                                    <Table sx={{ minWidth: 600 }}>
                                        <TableBody>
                                            <TableRow sx={{ background: darkMode ? "#2D3039" : "#f8f9fa" }}>
                                                <DefaultCell width="5%">
                                                    <Checkbox 
                                                        checked={selectedRowIds.size === previewList.length && previewList.length > 0} 
                                                        indeterminate={selectedRowIds.size > 0 && selectedRowIds.size < previewList.length}
                                                        onChange={handleToggleSelectAll}
                                                        size="small"
                                                        sx={{ color: darkMode ? "#fff" : undefined }}
                                                    />
                                                </DefaultCell>
                                                <DefaultCell width="10%"># Ficha</DefaultCell>
                                                <DefaultCell width="25%">Cliente</DefaultCell>
                                                <DefaultCell width="20%">Status Atual</DefaultCell>
                                                <DefaultCell width="15%">Saldo (R$)</DefaultCell>
                                                <DefaultCell width="15%">Últ. Agenda</DefaultCell>
                                                <DefaultCell width="15%">Responsável</DefaultCell>
                                            </TableRow>

                                            {previewList.map((item, idx) => {
                                                const isChecked = selectedRowIds.has(item.id);
                                                return (
                                                    <TableRow key={idx} hover sx={{ backgroundColor: isChecked ? (darkMode ? "rgba(0, 123, 255, 0.2)" : "#eef6ff") : "transparent" }}>
                                                        <DefaultCell>
                                                            <Checkbox 
                                                                checked={isChecked} 
                                                                onChange={() => handleToggleSelectRow(item.id)}
                                                                size="small" 
                                                                sx={{ color: darkMode ? "#fff" : undefined }}
                                                            />
                                                        </DefaultCell>
                                                        <DefaultCell><strong>{item.numeroFichaOuContrato}</strong></DefaultCell>
                                                        <DefaultCell>{item.cliente}</DefaultCell>
                                                        <DefaultCell>
                                                            <Chip label={item.statusAtual} size="small" variant="outlined" sx={{ color: darkMode ? "#fff" : "inherit", borderColor: darkMode ? "rgba(255,255,255,0.4)" : undefined }} />
                                                        </DefaultCell>
                                                        <DefaultCell>
                                                            R$ {Number(item.saldo || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                                                        </DefaultCell>
                                                        <DefaultCell>{item.ultimoAgendamento}</DefaultCell>
                                                        <DefaultCell>{item.responsavel}</DefaultCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </MDBox>
                            )}
                        </Card>
                    </Grid>

                    {/* COLUNA DIREITA: PAINEL DE AÇÃO "MARCAÇÃO EM BLOCO" & RESUMO */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ p: 2, background: darkMode ? "#424550" : "#ffffff", minHeight: 420, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                            <MDBox>
                                <MDTypography variant="h6" color={darkMode ? "white" : "dark"} display="flex" alignItems="center" gap={1} mb={2}>
                                    <Icon color="info">bookmark</Icon> Marcação em Bloco
                                </MDTypography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <InputLabel sx={{ color: darkMode ? "#ffffff !important" : "inherit", fontWeight: "bold", mb: 0.5 }}>Marcação Especial</InputLabel>
                                        <Select
                                            fullWidth
                                            size="small"
                                            value={selectedMarcacaoId}
                                            onChange={(e) => setSelectedMarcacaoId(e.target.value)}
                                            sx={{
                                                height: 40,
                                                backgroundColor: darkMode ? "#2D3039" : "#ffffff",
                                                color: darkMode ? "#ffffff !important" : "inherit",
                                                "& .MuiSelect-icon": { color: darkMode ? "#ffffff !important" : "inherit" },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: darkMode ? "#007bff !important" : undefined
                                                }
                                            }}
                                        >
                                            <MenuItem value="">Selecione uma Marcação...</MenuItem>
                                            {marcacoesList.map(m => (
                                                <MenuItem key={m.cvId} value={m.cvId}>{m.ccNome}</MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <InputLabel sx={{ color: darkMode ? "#ffffff !important" : "inherit", fontWeight: "bold", mb: 0.5 }}>Ação Desejada</InputLabel>
                                        <MDBox display="flex" gap={1}>
                                            <MDButton
                                                fullWidth
                                                size="small"
                                                variant={tipoAcao === "ADICIONAR" ? "contained" : "outlined"}
                                                color="success"
                                                onClick={() => setTipoAcao("ADICIONAR")}
                                                sx={{
                                                    fontWeight: "bold",
                                                    backgroundColor: tipoAcao === "ADICIONAR" ? "#2e7d32 !important" : (darkMode ? "rgba(255,255,255,0.05)" : undefined),
                                                    color: tipoAcao === "ADICIONAR" ? "#ffffff !important" : (darkMode ? "#ffffff !important" : undefined),
                                                    borderColor: darkMode ? "rgba(255,255,255,0.4) !important" : undefined
                                                }}
                                            >
                                                ➕ Adicionar Marcação
                                            </MDButton>
                                            <MDButton
                                                fullWidth
                                                size="small"
                                                variant={tipoAcao === "REMOVER" ? "contained" : "outlined"}
                                                color="error"
                                                onClick={() => setTipoAcao("REMOVER")}
                                                sx={{
                                                    fontWeight: "bold",
                                                    backgroundColor: tipoAcao === "REMOVER" ? "#d32f2f !important" : (darkMode ? "rgba(255,255,255,0.05)" : undefined),
                                                    color: tipoAcao === "REMOVER" ? "#ffffff !important" : (darkMode ? "#ffffff !important" : undefined),
                                                    borderColor: darkMode ? "rgba(255,255,255,0.4) !important" : undefined
                                                }}
                                            >
                                                ➖ Remover Marcação
                                            </MDButton>
                                        </MDBox>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <InputLabel sx={{ color: darkMode ? "#ffffff !important" : "inherit", fontWeight: "bold", mb: 0.5 }}>Observação (opcional)</InputLabel>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            placeholder="Digite uma observação que será registrada no histórico..."
                                            value={observacao}
                                            onChange={(e) => setObservacao(e.target.value)}
                                            sx={{
                                                backgroundColor: darkMode ? "#2D3039" : "#ffffff",
                                                borderRadius: 1,
                                                "& .MuiInputBase-input": { color: darkMode ? "#ffffff !important" : "inherit" },
                                                "& .MuiInputBase-input::placeholder": {
                                                    color: darkMode ? "rgba(255, 255, 255, 0.6) !important" : "inherit",
                                                    opacity: 1
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: darkMode ? "#007bff !important" : undefined
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                {/* CARD DE RESUMO DA OPERAÇÃO */}
                                <Card variant="outlined" sx={{ mt: 2, p: 1.5, background: darkMode ? "#2D3039" : "#eef7ee", borderColor: darkMode ? "#555" : "#c8e6c9" }}>
                                    <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} display="flex" alignItems="center" gap={0.5} mb={1}>
                                        <Icon color="success" fontSize="small">check_circle</Icon> Resumo da Operação
                                    </MDTypography>
                                    <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "text.main" }}>Fichas Encontradas:</MDTypography>
                                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>{previewList.length}</MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "text.main" }}>Fichas Selecionadas:</MDTypography>
                                        <MDTypography variant="caption" fontWeight="bold" color="info" sx={{ color: darkMode ? "#90caf9 !important" : undefined }}>{selectedRowIds.size}</MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "text.main" }}>Marcação a Aplicar:</MDTypography>
                                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                            {selectedMarcacaoObj ? selectedMarcacaoObj.ccNome : "-"}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" justifyContent="space-between">
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "text.main" }}>Responsável:</MDTypography>
                                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>{userName || "Operador"}</MDTypography>
                                    </MDBox>
                                </Card>
                            </MDBox>

                            <MDButton 
                                variant="contained" 
                                color="info" 
                                fullWidth
                                onClick={handleAplicarLote} 
                                disabled={loadingExecucao || selectedRowIds.size === 0 || !selectedMarcacaoId}
                                sx={{
                                    mt: 2,
                                    backgroundColor: darkMode ? "#007bff !important" : undefined,
                                    color: "#ffffff !important",
                                    fontWeight: "bold",
                                    py: 1.2,
                                    boxShadow: darkMode ? "0 4px 12px rgba(0, 123, 255, 0.4)" : undefined
                                }}
                            >
                                <Icon sx={{ mr: 0.5 }}>play_arrow</Icon>
                                {loadingExecucao ? "Aplicando..." : `Aplicar ${tipoAcao === "ADICIONAR" ? "Adição" : "Remoção"} em Lote`}
                            </MDButton>
                        </Card>
                    </Grid>
                </Grid>

                {/* 3. HISTÓRICO DE ÚLTIMAS MARCAÇÕES EM BLOCO (SEÇÃO INFERIOR) */}
                <Accordion sx={{ mt: 2, background: darkMode ? "#424550" : "#ffffff", boxShadow: "none", border: darkMode ? "1px solid #555" : "1px solid #e0e0e0" }} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: darkMode ? "#fff" : "#000" }} />}>
                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"} display="flex" alignItems="center" gap={1}>
                            <Icon color="info">history</Icon> Últimas Marcações em Bloco (Histórico de Execuções)
                        </MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {historicoLogs.length === 0 ? (
                            <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                                Nenhum histórico de marcação em bloco registrado recentemente.
                            </MDTypography>
                        ) : (
                            <MDBox overflow="auto" maxHeight={240}>
                                <Table sx={{ minWidth: 700 }}>
                                    <TableBody>
                                        <TableRow sx={{ background: darkMode ? "#2D3039" : "#f8f9fa" }}>
                                            <DefaultCell width="15%">Data / Hora</DefaultCell>
                                            <DefaultCell width="15%">Usuário</DefaultCell>
                                            <DefaultCell width="20%">Marcação</DefaultCell>
                                            <DefaultCell width="10%">Ação</DefaultCell>
                                            <DefaultCell width="10%">Qtde. Envia</DefaultCell>
                                            <DefaultCell width="10%">Qtde. Marcada</DefaultCell>
                                            <DefaultCell width="10%">Status</DefaultCell>
                                            <DefaultCell width="10%">Ações</DefaultCell>
                                        </TableRow>

                                        {historicoLogs.map((log, idx) => (
                                            <TableRow key={idx} hover>
                                                <DefaultCell>{log.dataHora}</DefaultCell>
                                                <DefaultCell>{log.usuario}</DefaultCell>
                                                <DefaultCell><strong>{log.marcacaoNome}</strong></DefaultCell>
                                                <DefaultCell>
                                                    <Chip 
                                                        label={log.tipoAcao} 
                                                        color={log.tipoAcao === "REMOVER" ? "error" : "success"} 
                                                        size="small" 
                                                        variant="outlined" 
                                                        sx={{ color: darkMode ? "#fff" : undefined, borderColor: darkMode ? "rgba(255,255,255,0.4)" : undefined }}
                                                    />
                                                </DefaultCell>
                                                <DefaultCell>{log.qtdeEnviada}</DefaultCell>
                                                <DefaultCell><strong>{log.qtdeMarcada}</strong></DefaultCell>
                                                <DefaultCell>{getStatusChip(log.status)}</DefaultCell>
                                                <DefaultCell>
                                                    <Tooltip title="Ver Detalhes do Log">
                                                        <IconButton onClick={() => { setSelectedLogDetail(log); setLogDetailOpen(true); }} sx={{ color: darkMode ? "#90caf9" : undefined }}>
                                                            <Icon color="info">visibility</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </DefaultCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </MDBox>
                        )}
                    </AccordionDetails>
                </Accordion>
            </DialogContent>

            <DialogActions sx={{ p: 2, backgroundColor: darkMode ? "#424550" : "#ffffff", borderTop: darkMode ? "1px solid #555" : "1px solid #eee" }}>
                <MDButton variant="outlined" color="secondary" onClick={handleClose} sx={{ color: darkMode ? "#ffffff !important" : undefined, borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined }}>
                    Fechar
                </MDButton>
            </DialogActions>

            {/* MODAL DE DETALHES DO HISTÓRICO */}
            <Dialog open={logDetailOpen} onClose={() => setLogDetailOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ backgroundColor: darkMode ? "#424550" : "#fff", color: darkMode ? "#fff" : "inherit" }}>
                    Detalhes do Log de Execução #{selectedLogDetail?.id}
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: darkMode ? "#363945" : "#fff", color: darkMode ? "#fff" : "inherit" }}>
                    {selectedLogDetail && (
                        <Grid container spacing={1.5}>
                            <Grid item xs={6}><strong>Data/Hora:</strong> {selectedLogDetail.dataHora}</Grid>
                            <Grid item xs={6}><strong>Usuário:</strong> {selectedLogDetail.usuario}</Grid>
                            <Grid item xs={6}><strong>Marcação:</strong> {selectedLogDetail.marcacaoNome}</Grid>
                            <Grid item xs={6}><strong>Ação:</strong> {selectedLogDetail.tipoAcao}</Grid>
                            <Grid item xs={4}><strong>Enviadas:</strong> {selectedLogDetail.qtdeEnviada}</Grid>
                            <Grid item xs={4}><strong>Marcadas:</strong> {selectedLogDetail.qtdeMarcada}</Grid>
                            <Grid item xs={4}><strong>Não Marcadas:</strong> {selectedLogDetail.qtdeNaoMarcada}</Grid>
                            <Grid item xs={12} mt={1}>
                                <strong>Observação:</strong>
                                <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                                    {selectedLogDetail.observacao || "Nenhuma observação informada."}
                                </MDTypography>
                            </Grid>
                            {selectedLogDetail.detalhesErros && (
                                <Grid item xs={12} mt={1}>
                                    <strong>Detalhes / Pendências:</strong>
                                    <MDBox 
                                        sx={{ 
                                            background: darkMode ? "#222" : "#fff0f0", 
                                            p: 1, 
                                            borderRadius: 1, 
                                            fontFamily: "monospace", 
                                            fontSize: 12,
                                            whiteSpace: "pre-wrap"
                                        }}
                                    >
                                        {selectedLogDetail.detalhesErros}
                                    </MDBox>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ backgroundColor: darkMode ? "#424550" : "#fff" }}>
                    <MDButton variant="contained" color="info" onClick={() => setLogDetailOpen(false)}>
                        Fechar
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* NOTIFICAÇÃO TOAST */}
            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Atenção"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />
        </Dialog>
    );
}

// AvatarStep Helper Component
function AvatarStep({ number, active, current, label, darkMode }) {
    return (
        <MDBox display="flex" alignItems="center" gap={0.5}>
            <MDBox
                sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: active ? (current ? "#007bff" : "#2e7d32") : (darkMode ? "#5D616D" : "#ccc"),
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: "bold"
                }}
            >
                {number}
            </MDBox>
            <MDTypography 
                variant="caption" 
                fontWeight={current ? "bold" : "regular"} 
                sx={{ color: active ? (darkMode ? "#ffffff" : "inherit") : (darkMode ? "#b0b8c4" : "#666") }}
            >
                {label}
            </MDTypography>
        </MDBox>
    );
}
